import { Feature } from 'ol';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Geometry } from 'ol/geom';
import Polygon from 'ol/geom/Polygon';
import { useMemo } from 'react';
import { MapInstance } from '@/types/map';
import { useOlMapProcessSource } from '@/components/Map/MapViewer/utils/config/processLayer/useOlMapProcessSource';
import { useOlMapPinsSource } from '@/components/Map/MapViewer/utils/config/pinsLayer/useOlMapPinsSource';
import { useOlMapReactionsSource } from '@/components/Map/MapViewer/utils/config/reactionsLayer/useOlMapReactionsSource';
import { useOlMapCommentsSource } from '@/components/Map/MapViewer/utils/config/commentsLayer/useOlMapCommentsSource';
import { useOlMapCardSource } from '@/components/Map/MapViewer/utils/config/mapCardLayer/useOlMapCardSource';
import { useOlMapAdditionalLayers } from '@/components/Map/MapViewer/utils/config/additionalLayers/useOlMapAdditionalLayers';
import { useOlMapMarkersSource } from '@/components/Map/MapViewer/utils/config/markersLayer/useOlMapMarkersSource';

export interface OlMapSources {
  cardSource: VectorSource<Feature<Polygon>>;
  processSource: VectorSource<Feature>;
  pinsSource: VectorSource<Feature<Geometry>>;
  reactionsSource: VectorSource<Feature<Geometry>>;
  commentsSource: VectorSource<Feature<Geometry>>;
  markersSource: VectorSource<Feature<Geometry>>;
  /**
   * The main map's user-drawn (additional) layers. They are built once here —
   * including their editing interactions, bound to the primary map — and the
   * loupe reuses each layer's source to render read-only copies.
   */
  additionalLayers: VectorLayer[];
}

/**
 * Owns the store-driven {@link VectorSource}s shared by every map that renders
 * the diagram. These sources are map-independent (geometry in projection space)
 * so they can be wrapped in per-map layer objects via the `create*Layer`
 * factories — e.g. the main map and the magnifier loupe rendering the exact
 * same source instances, with no duplicated feature building.
 *
 * `mapInstance` is the primary map; the process source uses it for the
 * view-scale guard and feature construction.
 */
export const useOlMapSources = ({ mapInstance }: { mapInstance: MapInstance }): OlMapSources => {
  const cardSource = useOlMapCardSource();
  const processSource = useOlMapProcessSource({ mapInstance });
  const pinsSource = useOlMapPinsSource();
  const reactionsSource = useOlMapReactionsSource();
  const commentsSource = useOlMapCommentsSource();
  const markersSource = useOlMapMarkersSource({ mapInstance });
  const additionalLayers = useOlMapAdditionalLayers(mapInstance);

  // The source instances are stable (each `use*Source` memoizes its VectorSource
  // for the lifetime of the map), so memoize the aggregate object too — otherwise
  // every unrelated store update would hand consumers a new object identity and,
  // for the loupe, needlessly rebuild its layer stack.
  return useMemo(
    () => ({
      cardSource,
      processSource,
      pinsSource,
      reactionsSource,
      commentsSource,
      markersSource,
      additionalLayers,
    }),
    [
      cardSource,
      processSource,
      pinsSource,
      reactionsSource,
      commentsSource,
      markersSource,
      additionalLayers,
    ],
  );
};
